<template>
    <div class="av-container">
        <div
            id="player-container"
            class="JARVIS_MatchDetail_View_LiveVideo"
            v-bind:class="{ 'has-error': errorMsg }"
        />
        <div v-if="errorMsg" class="error-container">
            {{ errorMsg }}
        </div>
    </div>
</template>

<script lang="ts">
    import {
        defineComponent,
        onMounted,
        onUnmounted,
        ref,
    } from 'vue';
    import { LiveStreamResource } from '@/interface/enum';
    import Api from '@/libraries/api';
    import { loadCss, loadScriptWithError } from '@/libraries/utils/dom';

    interface AvvplPlayer {
        on(event: string, cb: (e: any) => void): void;
        load(url: string): void;
        remove(): void;
    }

    type AvvplPlayerConstructor = new (config: any) => AvvplPlayer;

    declare let avvpl: { setupPlayer: AvvplPlayerConstructor };

    async function setupPlayer() {
        loadCss('https://avplayer-cdn.sportradar.com/dist/latest/styles.css');
        await loadScriptWithError('https://avplayer-cdn.sportradar.com/dist/latest/avvpl-player.js', 'omit');

        // eslint-disable-next-line no-undef, new-cap
        const player = new avvpl.setupPlayer({ id: 'player-container' });
        player.on('error', e => console.error(e));
        return player;
    }

    export default defineComponent({
        name: 'BetRadarStream',
        props: {
            matchId: {
                type: Number,
                required: true,
            },
            streamId: {
                type: Number,
                required: true,
            },
        },
        setup(props) {
            const errorMsg = ref('');

            onMounted(async () => {
                try {
                    const { matchId, streamId } = props;
                    const { url, errorCode, errorMessage } = await Api.getLiveStream(matchId, LiveStreamResource.BetRadar, streamId);
                    if (errorCode !== 0) {
                        throw new Error(`${errorMessage} (${errorCode})`);
                    }
                    const player = await setupPlayer();
                    onUnmounted(() => player.remove());

                    if (url) {
                        player.load(url);
                    }
                } catch (err) {
                    console.error(err);
                    if (err instanceof Error) {
                        errorMsg.value = err.message;
                    } else {
                        errorMsg.value = String(err);
                    }
                }
            });

            return {
                errorMsg,
            };
        },
    });
</script>

<style lang="scss" scoped>
.av-container {
    position: relative;
}

#player-container.has-error {
    min-height: 56.25vw;
    background-color: #000000;
}

.error-container {
    position: absolute;
    top: 0;
    display: flex;
    width: 100%;
    height: 56.25vw;
    align-items: center;
    justify-content: center;
    padding: 3em;
    background-color: rgba(0, 0, 0, 80%);
    color: white;
}
</style>
